import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import creditcardutils from 'creditcardutils';

import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  useCreateCardMutation,
  useUpdateCardMutation,
} from '../../services/orderApi';
import type { SavedCard } from '../../../../types/order.types';
import {
  NAME_PATTERN,
  formatCardNumber,
  resolveCardType,
  cvcLengthForType,
  displayCardType,
  isUpdateButtonDisabled,
  getEditDisplayValue,
} from '../utils/cardFormUtils';
import { useSelectedOrgName } from '@/hooks/useEntityScope';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseCardFormOptions {
  /** Whether the modal is currently open. Drives the reset/seed effect: the
   *  modal stays mounted (PaymentPage renders it unconditionally), so the form
   *  must be reset each time it opens rather than relying on a fresh mount. */
  open: boolean;
  isUpdate: boolean;
  isSingleCard: boolean;
  cardDetails?: SavedCard;
  onSuccess: () => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCardForm({
  open,
  isUpdate,
  isSingleCard,
  cardDetails,
  onSuccess,
  onClose,
}: UseCardFormOptions) {
  const dispatch = useDispatch();
  // One scope source rather than a second reader of it (DV-3013 §2.1 class B).
  const selectedOrgName = useSelectedOrgName();
  const [createCard, { isLoading: isCreating }] = useCreateCardMutation();
  const [updateCard, { isLoading: isUpdating }] = useUpdateCardMutation();

  const inputNumberRef = useRef<HTMLInputElement>(null);
  const inputCVCRef = useRef<HTMLInputElement>(null);
  const inputMonthRef = useRef<HTMLInputElement>(null);
  const inputYearRef = useRef<HTMLInputElement>(null);

  // Field state
  const [cardNumber, setCardNumber] = useState('');
  const [cardCVC, setCardCVC] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [defaultCard, setDefaultCard] = useState(false);
  const [terms, setTerms] = useState(false);

  // Combined `MM/YY` for downstream consumers (validation, save payload,
  // isButtonDisabled). Derived from the two split inputs.
  const cardExpiryDate = expiryMonth && expiryYear
    ? `${expiryMonth.padStart(2, '0')}/${expiryYear}`
    : expiryMonth || (expiryYear ? `/${expiryYear}` : '');

  // Derived
  const [detectedCardType, setDetectedCardType] = useState<string>('');
  const [cvcMaxLength, setCvcMaxLength] = useState(3);

  // Validation
  const [isValidCCNumber, setIsValidCCNumber] = useState(true);
  const [isValidCardCVC, setIsValidCardCVC] = useState(true);
  const [isValidCCName, setIsValidCCName] = useState(true);
  const [isValidCardExpiryDate, setIsValidCardExpiryDate] = useState(true);
  const [isValidZipCode, setIsValidZipCode] = useState(true);
  const [isValidTerms, setIsValidTerms] = useState(false);

  const loading = isCreating || isUpdating;

  // Reset (and, in edit mode, seed) the form each time the modal OPENS.
  // DV-2657: the modal is rendered unconditionally by PaymentPage, so the hook
  // never unmounts — without this reset the previous session's entries (card
  // number, expiry, and the stale cvc/name/zip state behind the uncontrolled
  // inputs) would carry into the next open. Keyed on `open` rather than mount
  // so both a fresh add and re-editing a different card start clean.
  useEffect(() => {
    if (!open) return;
    // Clean slate.
    setCardNumber('');
    setCardCVC('');
    setCardName('');
    setExpiryMonth('');
    setExpiryYear('');
    setZipCode('');
    setDefaultCard(false);
    setTerms(false);
    setDetectedCardType('');
    setCvcMaxLength(3);
    setIsValidCCNumber(true);
    setIsValidCardCVC(true);
    setIsValidCCName(true);
    setIsValidCardExpiryDate(true);
    setIsValidZipCode(true);
    setIsValidTerms(false);
    // Edit mode: seed the display/payload fields from the card being edited.
    if (isUpdate && cardDetails) {
      setDefaultCard(cardDetails.default);
      setTerms(true);
      const name = cardDetails.lastName
        ? `${cardDetails.firstName} ${cardDetails.lastName}`
        : cardDetails.firstName;
      setCardName(name);
      setExpiryMonth(String(cardDetails.expirationMonth ?? '').padStart(2, '0'));
      setExpiryYear(String(cardDetails.expirationYear ?? '').slice(-2));
      detectCardType(cardDetails.cardType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Validate CVC when type or value changes
  useEffect(() => {
    if (cardCVC && detectedCardType) {
      setIsValidCardCVC(creditcardutils.validateCardCVC(cardCVC, detectedCardType));
    }
  }, [cardCVC, detectedCardType]);

  // Detect card type from number
  useEffect(() => {
    if (!isUpdate && cardNumber) {
      const parsed = creditcardutils.parseCardType(cardNumber);
      if (parsed) setDetectedCardType(parsed);
    }
  }, [cardNumber, isUpdate]);

  // Update CVC max length based on card type
  useEffect(() => {
    setCvcMaxLength(cvcLengthForType(detectedCardType));
  }, [detectedCardType]);

  function detectCardType(type: string) {
    const resolved = resolveCardType(type);
    if (resolved) {
      setDetectedCardType(resolved);
      setCvcMaxLength(cvcLengthForType(resolved));
    }
  }

  function handleCardNumberChange(value: string) {
    const formatted = formatCardNumber(value);
    setCardNumber(formatted);
  }

  function handleCardNumberKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const input = inputNumberRef.current?.value ?? '';
      const newValue = e.key === 'Backspace' ? input.slice(0, -1) : input;
      const formatted = formatCardNumber(newValue);
      setCardNumber(formatted);
      if (formatted === '' && inputCVCRef.current) {
        inputCVCRef.current.value = '';
      }
    }
  }

  // ---- Expiry month / year (split fields) ----------------------------------

  function handleMonthInput(raw: string) {
    let digits = raw.replace(/\D/g, '').substring(0, 2);
    if (digits.length === 2) {
      const month = parseInt(digits, 10);
      if (month === 0) digits = '01';
      else if (month > 12) digits = '12';
    }
    setExpiryMonth(digits);
    if (!isValidCardExpiryDate) setIsValidCardExpiryDate(true);
  }

  // Auto-pad single-digit month + advance focus to year on '/' or Tab.
  function handleMonthKeyDown(e: React.KeyboardEvent) {
    if (e.key !== '/' && e.key !== 'Tab') return;
    if (expiryMonth.length === 1) {
      setExpiryMonth(expiryMonth.padStart(2, '0'));
    }
    if (e.key === '/') {
      e.preventDefault();
      inputYearRef.current?.focus();
    }
  }

  function handleYearInput(raw: string) {
    setExpiryYear(raw.replace(/\D/g, '').substring(0, 2));
    if (!isValidCardExpiryDate) setIsValidCardExpiryDate(true);
  }

  function validateExpiry(monthOverride?: string, yearOverride?: string) {
    const month = (monthOverride ?? expiryMonth).padStart(2, '0');
    const year = yearOverride ?? expiryYear;
    if (month.length !== 2 || year.length !== 2) {
      setIsValidCardExpiryDate(false);
      return;
    }
    setIsValidCardExpiryDate(creditcardutils.validateCardExpiry(month, year));
  }

  function handleMonthBlur() {
    let normalized = expiryMonth;
    if (expiryMonth.length === 1) {
      normalized = expiryMonth.padStart(2, '0');
      setExpiryMonth(normalized);
    }
    if (expiryYear) validateExpiry(normalized, expiryYear);
  }

  function handleYearBlur() {
    validateExpiry();
  }

  // Cap pasted card numbers at 16 digits (the longest accepted brand),
  // counting digits only — a paste of a formatted number ("4111 1111 …")
  // must not lose digits to its own spaces.
  function handlePaste(e: React.ClipboardEvent) {
    const input = e.currentTarget as HTMLInputElement;
    const pastedDigits = e.clipboardData.getData('text').replace(/\D/g, '');
    const currentDigits = input.value.replace(/\D/g, '');
    if (currentDigits.length + pastedDigits.length > 16) {
      e.preventDefault();
      const newValue =
        currentDigits +
        pastedDigits.substring(0, 16 - currentDigits.length);
      setCardNumber(formatCardNumber(newValue));
    }
  }

  function isButtonDisabled(): boolean {
    return isUpdateButtonDisabled(isUpdate, cardDetails, cardName, cardExpiryDate, defaultCard, cardCVC, zipCode, cardNumber);
  }

  function validate(): boolean {
    let valid = true;

    if (isUpdate) {
      if (!cardExpiryDate) { setIsValidCardExpiryDate(false); valid = false; }
      else { setIsValidCardExpiryDate(true); }

      if (!cardName || !NAME_PATTERN.test(cardName)) { setIsValidCCName(false); valid = false; }
      else { setIsValidCCName(true); }
    } else {
      // Type-aware length (15 for Amex, 16 for Visa/MC/Discover) + Luhn.
      // A flat `length < 16` check here used to reject every valid Amex card.
      if (!cardNumber || !creditcardutils.validateCardNumber(cardNumber)) {
        setIsValidCCNumber(false); valid = false;
      } else { setIsValidCCNumber(true); }

      if (!cardExpiryDate) { setIsValidCardExpiryDate(false); valid = false; }
      else { setIsValidCardExpiryDate(true); }

      setIsValidCardCVC(creditcardutils.validateCardCVC(cardCVC, detectedCardType));
      if (!creditcardutils.validateCardCVC(cardCVC, detectedCardType)) valid = false;

      if (!zipCode) { setIsValidZipCode(false); valid = false; }
      else { setIsValidZipCode(true); }

      if (!cardName || !NAME_PATTERN.test(cardName)) { setIsValidCCName(false); valid = false; }
      else { setIsValidCCName(true); }

      setIsValidTerms(!terms);
    }
    return valid;
  }

  async function onSave() {
    if (!validate()) return;

    const [month, year] = (cardExpiryDate ?? '').split('/');
    const validDate = creditcardutils.validateCardExpiry(month, year);
    setIsValidCardExpiryDate(validDate);
    if (!terms || !validDate) return;

    const expiryParsed = creditcardutils.parseCardExpiry(cardExpiryDate ?? '') as { month: number; year: number };

    try {
      if (isUpdate && cardDetails) {
        await updateCard({
          customerPaymentProfileId: cardDetails.customerPaymentProfileId,
          customerProfileId: cardDetails.customerProfileId,
          firstName: cardName,
          isDefault: defaultCard,
          expirationMonth: (cardExpiryDate ?? '').split('/')[0],
          expirationYear: expiryParsed.year.toString(),
          entity: selectedOrgName,
        }).unwrap();
        onSuccess();
        onClose();
      } else {
        await createCard({
          cardNumber: parseInt(cardNumber.replaceAll(' ', ''), 10),
          cardCode: cardCVC,
          firstName: cardName,
          cardType: detectedCardType,
          zipCode,
          expirationMonth: (cardExpiryDate ?? '').split('/')[0],
          expirationYear: expiryParsed.year.toString(),
          entity: selectedOrgName,
          isDefault: defaultCard,
        }).unwrap();
        onSuccess();
        onClose();
      }
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      if (error?.data?.message?.includes('The credit card number is invalid.')) {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Invalid card details' }));
      } else {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Something went wrong, Please contact admin' }));
      }
      if (isUpdate) onClose();
    }
  }

  return {
    // Refs
    inputNumberRef,
    inputCVCRef,
    inputMonthRef,
    inputYearRef,
    // State
    cardNumber,
    cardCVC,
    setCardCVC,
    cardName,
    setCardName,
    expiryMonth,
    expiryYear,
    cardExpiryDate,
    zipCode,
    setZipCode,
    defaultCard,
    setDefaultCard,
    terms,
    setTerms,
    cvcMaxLength,
    loading,
    // Derived
    displayCardType: displayCardType(isUpdate, detectedCardType, cardDetails?.cardType),
    // Validation
    isValidCCNumber,
    isValidCardCVC,
    isValidCCName,
    isValidCardExpiryDate,
    isValidZipCode,
    isValidTerms,
    // Handlers
    handleCardNumberChange,
    handleCardNumberKeyDown,
    handleMonthInput,
    handleMonthKeyDown,
    handleMonthBlur,
    handleYearInput,
    handleYearBlur,
    handlePaste,
    isButtonDisabled,
    getEditDisplayValue: (field: Parameters<typeof getEditDisplayValue>[2]) =>
      getEditDisplayValue(isUpdate, cardDetails, field),
    onSave,
    // Flags
    isUpdate,
    isSingleCard,
  };
}
